/**
 * IXVerify v2 — the one date-of-birth format (owner rule, 2026-09-29).
 *
 * Every DOB a person reads or types in IXVerify is written 01Dec2020: a
 * two-digit day, the three-letter month, the four-digit year, nothing in
 * between. A slash date is never shown and never accepted from the keyboard
 * here — 01/12/2020 is the first of December in one country and the twelfth
 * of January in another, and the CRC reads this value out loud to the patient.
 *
 * The wire and the database keep ISO yyyy-mm-dd (the server's normalizeDob);
 * this module is the only place the wizard and the roster turn one into the
 * other. Pure: no IO, no React. DOB is PHI (R3) — nothing here logs.
 */

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const;
const MONTH_NAMES = [
  'january', 'february', 'march', 'april', 'may', 'june',
  'july', 'august', 'september', 'october', 'november', 'december',
] as const;

/** The example every DOB field shows (placeholder + hint). */
export const DOB_FORMAT_EXAMPLE = '01Dec2020';

const ISO_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
// Day, month name, year. Spaces, hyphens, dots or slashes between the parts
// are forgiven ("1 Dec 2020", "01-DEC-2020"); the month must be a word.
const DAY_MONTH_YEAR_RE = /^(\d{1,2})[\s./-]*([A-Za-z]{3,9})\.?[\s./-]*(\d{4})$/;

const pad2 = (n: number) => String(n).padStart(2, '0');

/** 1–12 for a month name or any prefix of three letters or more ("Dec", "Sept", "December"). */
function monthNumber(word: string): number | null {
  const w = word.toLowerCase();
  if (w.length < 3) return null;
  const i = MONTH_NAMES.findIndex((name) => name.startsWith(w));
  return i === -1 ? null : i + 1;
}

/**
 * ISO '1980-12-01' → '01Dec1980'. Reads the parts, never `new Date()`, so a
 * time zone can never move the day. A value that is not an ISO date comes
 * back unchanged (a DOB on file is never hidden); empty → ''.
 */
export function formatDob(value: string | null | undefined): string {
  const s = String(value ?? '').trim();
  const m = s.match(ISO_RE);
  if (!m) return s;
  const month = Number(m[2]);
  const day = Number(m[3]);
  if (month < 1 || month > 12 || day < 1 || day > 31) return s;
  return `${m[3]}${MONTHS[month - 1]}${m[1]}`;
}

/**
 * What a person typed → ISO yyyy-mm-dd, or null. Accepts 01Dec2020 (case,
 * a one-digit day, a full month name and separators forgiven) and ISO
 * 2020-12-01. Rejects slash dates, impossible days (31Feb) and anything
 * outside 1900..today — the same window the server enforces.
 */
export function parseDob(text: string | null | undefined, now: Date = new Date()): string | null {
  const s = String(text ?? '').trim();
  if (!s) return null;

  let y: number;
  let m: number;
  let d: number;
  const dmy = s.match(DAY_MONTH_YEAR_RE);
  const iso = dmy ? null : s.match(ISO_RE);
  if (dmy) {
    const month = monthNumber(dmy[2]);
    if (!month) return null;
    d = Number(dmy[1]);
    m = month;
    y = Number(dmy[3]);
  } else if (iso) {
    y = Number(iso[1]);
    m = Number(iso[2]);
    d = Number(iso[3]);
  } else {
    return null;
  }

  const date = new Date(Date.UTC(y, m - 1, d));
  const real = date.getUTCFullYear() === y && date.getUTCMonth() === m - 1 && date.getUTCDate() === d;
  if (!real || y < 1900 || date.getTime() > now.getTime()) return null;
  return `${y}-${pad2(m)}-${pad2(d)}`;
}
